import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ListingContext from '../../../../../../providers/Listing/Listing.context';
import { getDataRoomRoute, sentryError } from 'utils';
import api from 'api';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import useCallbackRef from 'hooks/useCallbackRef';
import { Link } from 'react-router-dom';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './AddCustomNdaModal.styles.scss';

type AddCustomNdaModalProps = {
  className?: string;
};

const AddCustomNdaModal: ForwardRefRenderFunction<
  OverlayRef,
  AddCustomNdaModalProps
> = (props, ref) => {
  const { className } = props;

  const { popSuccess } = useContext(PopNotificationsContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { hasUnlockPremiumFeatures, listing, setListing } =
    useContext(ListingContext);

  const {
    _id: listingId,
    dataRoomDocumentsAvailabilityAfterSignNDA,
    dataRoomId,
  } = listing || {};

  const classes = classNames('add-custom-nda-modal', className);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const hasDocumentsToUnlockAfterNDA =
    !!dataRoomDocumentsAvailabilityAfterSignNDA?.length;

  const documentOptions = useMemo(
    () =>
      hasDocumentsToUnlockAfterNDA
        ? dataRoomDocumentsAvailabilityAfterSignNDA.map(({ fileId, name }) => ({
            label: name,
            value: fileId,
          }))
        : [],
    [dataRoomDocumentsAvailabilityAfterSignNDA, hasDocumentsToUnlockAfterNDA],
  );

  const initialValues = useMemo(() => {
    if (!hasDocumentsToUnlockAfterNDA) return { documents: {} };

    const documents: Record<string, boolean> = {};

    (dataRoomDocumentsAvailabilityAfterSignNDA || [])?.forEach(
      ({ fileId, checked }) => (documents[fileId] = checked),
    );

    return {
      documents,
    };
  }, [dataRoomDocumentsAvailabilityAfterSignNDA, hasDocumentsToUnlockAfterNDA]);

  const handleDocumentsAfterNDA = useCallback(
    async (values) => {
      const { documents } = values;

      const parsedDocuments: any = [];

      Object.entries(documents || {}).forEach(([key, value]) => {
        if (value) parsedDocuments.push(key);
      });

      try {
        const { data } = await api.portfolio.seller.documentsToUnlockAfterNDA(
          listingId,
          parsedDocuments,
        );
        popSuccess(t('successfullyAppliedChanges'));
        setListing(data);
        modal.close();
      } catch (error) {
        sentryError(error);
      }
    },
    [modal, listingId, popSuccess, setListing, t],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t(
        hasDocumentsToUnlockAfterNDA
          ? 'chooseDocuments'
          : 'noDocumentsUploaded',
      )}
    >
      <Form
        onSubmit={handleDocumentsAfterNDA}
        initialValues={initialValues}
        render={({ handleSubmit, values, pristine, submitting, form }) => {
          const { documents } = values;

          const isAllChecked = Object.values(documents).every(Boolean);

          const handleSelectAll = () => {
            const newVal = isAllChecked ? false : true;

            const newDocuments: Record<string, boolean> = {};
            (dataRoomDocumentsAvailabilityAfterSignNDA || [])?.forEach(
              ({ fileId }) => (newDocuments[fileId] = newVal),
            );
            form.change('documents', newDocuments);
          };

          return (
            <form onSubmit={handleSubmit}>
              {hasDocumentsToUnlockAfterNDA ? (
                <div className="checkboxes">
                  <div>
                    <p>
                      <span>{t('documentsUnlockAfterNdaDescription')}</span>
                      {isSuperAdmin || hasUnlockPremiumFeatures ? (
                        <Link to={getDataRoomRoute(dataRoomId)}>Data Room</Link>
                      ) : (
                        'Data Room'
                      )}
                      {'. '}
                      <span>{t('documentsUnlockAfterNdaDescription2')}</span>
                    </p>

                    <p>{t('documentsUnlockAfterNdaDescriptionInfo')}</p>
                  </div>

                  <Button variant="link" onClick={handleSelectAll}>
                    {t(isAllChecked ? 'deselectAll' : 'selectAll')}
                  </Button>
                  <CheckboxFieldGroup
                    name="documents"
                    options={documentOptions}
                  />
                </div>
              ) : (
                <p>{t('noDocumentsFirstUpload')}</p>
              )}

              <div className="actions">
                <Button variant="link" onClick={modal?.close}>
                  {t('cancel')}
                </Button>
                {hasDocumentsToUnlockAfterNDA && (
                  <Button type="submit" disabled={pristine || submitting}>
                    {t('confirm')}
                  </Button>
                )}
              </div>
            </form>
          );
        }}
      />
    </Modal>
  );
};

export default forwardRef(AddCustomNdaModal);
